/* tslint:disable */
/* eslint-disable */
/**
 * Distributor API
 * The Cashlink Distributor API enables you to distribute crypto securities and digital securities. It contains all building blocks from investor registration and KYC/AML to brokerage, clearing and settlement processes. While you are in full control of the frontends served to your investors you can use the Cashlink Studio to monitor and manage the activity on your platform.   Note: You cannot access data of investors or investments if they haven\'t been created via the API with your provided API credentials. Also, it\'s not possible to access data of investors that have registered via the Cashlink Signing Module, if you are using this as well.  ### Authentication  Every call to the API must be authenticated via OAuth client credentials. During setup, your point of contact will provide you with your client ID and client secret.   ### Idempotency  For certain POST endpoints, the API uses client-supplied idempotency keys to ensure that requests can be retried safely without performing the same operation twice. Repeated calls to those endpoints with the same idempotency key will return the cached response from the first call. Keys are expected to be set as header parameters with *X-Idempotency-Key*. The recommended approach for interacting with such endpoints is as follows:   - Create a unique, random key, such as a UUIDv4.   - Store the intent to call an endpoint along with the generated key in the client database.   - Flush to disk (e.g. commit an SQL transaction).   - Perform the call to the endpoint.   This ensures that even if the response from the API to a client gets lost, or the client experiences any sort of outage between steps 3 and 4, the client can always safely retry the same action.   If you already have objects corresponding to tasks in the API in your client-side database, you can use their internal IDs as idempotency keys!   ### Monetary values   Monetary values are represented as a combination of amount, decimals & currency. For example, ```{\'amount\': \'40000000\', \'decimals\': 3, \'currency\': \'EUR\'} means \'EUR 40,000.000\'```.   ### Handling documents  The document endpoint can be used to upload documents. Files are uploaded via HTTP multipart. The endpoint will return a document ID that can be used in other requests to refer to that document.   ### Webhooks  You can also register webhooks so that we can notify your application about events with regards to investments. To use that, please provide your point of contact at Cashlink with the following data:   - URL to be used as the target for webhooks  - OAuth client credentials or bearer authentication token so that we can authenticate at your server.  Webhooks are sent every time the status of an investment changes.  **POST <customer-provided URL>**  Webhooks are retried until the called endpoint returns a 2xx.  **Request**  {  \'investment_id\': \'3fa85f64-5717-4562-b3fc-2c963f66afa6\',  \'status\': \'ACCEPTED\',  \'changed_at\': \'2020-12-11T14:59:46.716Z\'  }  ### Concepts  The Cashlink Distributor API is based on the following concepts:  - Persons (legal or natural) that are users on your platform willing to invest are called investors.  - These investors will be able to invest into different financial products issued by different entities. In the API they are called products.  - The purchase of units of such a product takes place within your application and ends with a signed investment contract.  - Now you can create an investment, which contains the investor, the units, the signed investment contract and the accepted date of the investment by the issuer.   - At some point, parts of the investments are returned to the issuer because the outstanding amount has been paid back for example. Such a transaction is called a redemption.    
 *
 * The version of the OpenAPI document: 2.22.0
 * Contact: support@cashlink.de
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import * as runtime from '../runtime';
import type {
  ExternalError,
  ExternalInvitation,
  ExternalInvitationCancellationRequest,
  ExternalInvitationRequest,
  GroupScore,
  InvestmentCalculation,
  InvestmentWalletAssociationRequest,
  InvestmentWalletSelectionRequest,
  InvestorCreation,
  InvestorCreationRequest,
  InvestorUpdateRequest,
  Invitation,
  InvitationOfferConfigurationRequest,
  InvitationSetQuestionnaireRequest,
  InvitationSetVibInfoRequest,
  InvitationTrackingRequest,
  KycInformation,
  ManagedAssetPreparation,
  PaginatedInvitationList,
  PatchedExternalInvitationRequest,
  QuestionnaireQuestion,
  SelfDisclosureCalculation,
  VibInfo,
  Voucher,
  WaiverRequest,
} from '../models/index';
import {
    ExternalErrorFromJSON,
    ExternalErrorToJSON,
    ExternalInvitationFromJSON,
    ExternalInvitationToJSON,
    ExternalInvitationCancellationRequestFromJSON,
    ExternalInvitationCancellationRequestToJSON,
    ExternalInvitationRequestFromJSON,
    ExternalInvitationRequestToJSON,
    GroupScoreFromJSON,
    GroupScoreToJSON,
    InvestmentCalculationFromJSON,
    InvestmentCalculationToJSON,
    InvestmentWalletAssociationRequestFromJSON,
    InvestmentWalletAssociationRequestToJSON,
    InvestmentWalletSelectionRequestFromJSON,
    InvestmentWalletSelectionRequestToJSON,
    InvestorCreationFromJSON,
    InvestorCreationToJSON,
    InvestorCreationRequestFromJSON,
    InvestorCreationRequestToJSON,
    InvestorUpdateRequestFromJSON,
    InvestorUpdateRequestToJSON,
    InvitationFromJSON,
    InvitationToJSON,
    InvitationOfferConfigurationRequestFromJSON,
    InvitationOfferConfigurationRequestToJSON,
    InvitationSetQuestionnaireRequestFromJSON,
    InvitationSetQuestionnaireRequestToJSON,
    InvitationSetVibInfoRequestFromJSON,
    InvitationSetVibInfoRequestToJSON,
    InvitationTrackingRequestFromJSON,
    InvitationTrackingRequestToJSON,
    KycInformationFromJSON,
    KycInformationToJSON,
    ManagedAssetPreparationFromJSON,
    ManagedAssetPreparationToJSON,
    PaginatedInvitationListFromJSON,
    PaginatedInvitationListToJSON,
    PatchedExternalInvitationRequestFromJSON,
    PatchedExternalInvitationRequestToJSON,
    QuestionnaireQuestionFromJSON,
    QuestionnaireQuestionToJSON,
    SelfDisclosureCalculationFromJSON,
    SelfDisclosureCalculationToJSON,
    VibInfoFromJSON,
    VibInfoToJSON,
    VoucherFromJSON,
    VoucherToJSON,
    WaiverRequestFromJSON,
    WaiverRequestToJSON,
} from '../models/index';

export interface ExternalDistributorV2InvitationsCancellationCreateRequest {
    id: string;
    externalInvitationCancellationRequest: ExternalInvitationCancellationRequest;
}

export interface ExternalDistributorV2InvitationsCreateRequest {
    xIdempotencyKey: string;
    externalInvitationRequest: ExternalInvitationRequest;
}

export interface ExternalDistributorV2InvitationsPartialUpdateRequest {
    xIdempotencyKey: string;
    id: string;
    patchedExternalInvitationRequest?: PatchedExternalInvitationRequest;
}

export interface ExternalDistributorV2InvitationsRetrieveRequest {
    id: string;
}

export interface ExternalDistributorV2InvitationsUpdateRequest {
    id: string;
    externalInvitationRequest: ExternalInvitationRequest;
}

export interface InvitationsAbortionCreateRequest {
    id: string;
}

export interface InvitationsAssetPreparationDestroyRequest {
    id: string;
}

export interface InvitationsAssetPreparationRetrieveRequest {
    id: string;
}

export interface InvitationsAssetPreparationUpdateRequest {
    id: string;
}

export interface InvitationsCalculationsRetrieveRequest {
    id: string;
    code?: string;
    requestedTokens?: number;
}

export interface InvitationsIdentificationRetrieveRequest {
    id: string;
}

export interface InvitationsIdentificationStartNotificationCreateRequest {
    id: string;
}

export interface InvitationsListRequest {
    campaignId?: string;
    invitationType?: string;
    isEcsp?: boolean;
    limit?: number;
    offset?: number;
    ordering?: string;
    status?: string;
}

export interface InvitationsOfferConfigurationCreateRequest {
    id: string;
    invitationOfferConfigurationRequest: InvitationOfferConfigurationRequest;
}

export interface InvitationsOfferCreateRequest {
    id: string;
}

export interface InvitationsPrefilledQuestionnaireListRequest {
    id: string;
    ordering?: string;
}

export interface InvitationsPrefilledVibInfoRetrieveRequest {
    id: string;
}

export interface InvitationsQuestionnaireCreateRequest {
    id: string;
    invitationSetQuestionnaireRequest: InvitationSetQuestionnaireRequest;
}

export interface InvitationsRegistrationCreateRequest {
    id: string;
    investorCreationRequest: InvestorCreationRequest;
}

export interface InvitationsRegistrationUpdateRequest {
    id: string;
    investorUpdateRequest?: InvestorUpdateRequest;
}

export interface InvitationsResetWalletSelectionCreateRequest {
    id: string;
}

export interface InvitationsRetrieveRequest {
    id: string;
}

export interface InvitationsSelfDisclosureRetrieveRequest {
    id: string;
    requestedTokens?: number;
}

export interface InvitationsSurveyScoreListRequest {
    id: string;
    ordering?: string;
}

export interface InvitationsTrackingInformationCreateRequest {
    id: string;
    invitationTrackingRequest: InvitationTrackingRequest;
}

export interface InvitationsUserAssociationCreateRequest {
    id: string;
}

export interface InvitationsVibInfoCreateRequest {
    id: string;
    invitationSetVibInfoRequest: InvitationSetVibInfoRequest;
}

export interface InvitationsVoucherRetrieveRequest {
    id: string;
    code?: string;
    requestedTokens?: number;
}

export interface InvitationsWaiversCreateRequest {
    id: string;
    waiverRequest?: WaiverRequest;
}

export interface InvitationsWalletAssociationCreateRequest {
    id: string;
    investmentWalletAssociationRequest: InvestmentWalletAssociationRequest;
}

export interface InvitationsWalletSelectionCreateRequest {
    id: string;
    investmentWalletSelectionRequest: InvestmentWalletSelectionRequest;
}

/**
 * 
 */
export class InvitationsApi extends runtime.BaseAPI {

    /**
     * Invitations can be canceled at any time if the invitation has not yet been used to create an investment by providing a cancelation date. After the invitation has been canceled it receives the status ABORTED.
     * Cancel an invitation
     */
    async externalDistributorV2InvitationsCancellationCreateRaw(requestParameters: ExternalDistributorV2InvitationsCancellationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<ExternalInvitation>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling externalDistributorV2InvitationsCancellationCreate().'
            );
        }

        if (requestParameters['externalInvitationCancellationRequest'] == null) {
            throw new runtime.RequiredError(
                'externalInvitationCancellationRequest',
                'Required parameter "externalInvitationCancellationRequest" was null or undefined when calling externalDistributorV2InvitationsCancellationCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        if (this.configuration && this.configuration.accessToken) {
            // oauth required
            headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", ["investments:write"]);
        }

        const response = await this.request({
            path: `/api/external/distributor/v2/invitations/{id}/cancellation/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: ExternalInvitationCancellationRequestToJSON(requestParameters['externalInvitationCancellationRequest']),
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => ExternalInvitationFromJSON(jsonValue));
    }

    /**
     * Invitations can be canceled at any time if the invitation has not yet been used to create an investment by providing a cancelation date. After the invitation has been canceled it receives the status ABORTED.
     * Cancel an invitation
     */
    async externalDistributorV2InvitationsCancellationCreate(requestParameters: ExternalDistributorV2InvitationsCancellationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<ExternalInvitation> {
        const response = await this.externalDistributorV2InvitationsCancellationCreateRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Use this endpoint to create invitations for investors to invest into a product. After all needed data is present in the invitation, it can be processed into an investment.  Invitations can only be used if you are using the Investment Platform API offering. If you\'re using the Distributor API product offering you can directly create investments without the need for invitations.
     * Create an invitation
     */
    async externalDistributorV2InvitationsCreateRaw(requestParameters: ExternalDistributorV2InvitationsCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<ExternalInvitation>> {
        if (requestParameters['xIdempotencyKey'] == null) {
            throw new runtime.RequiredError(
                'xIdempotencyKey',
                'Required parameter "xIdempotencyKey" was null or undefined when calling externalDistributorV2InvitationsCreate().'
            );
        }

        if (requestParameters['externalInvitationRequest'] == null) {
            throw new runtime.RequiredError(
                'externalInvitationRequest',
                'Required parameter "externalInvitationRequest" was null or undefined when calling externalDistributorV2InvitationsCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        if (requestParameters['xIdempotencyKey'] != null) {
            headerParameters['X-Idempotency-Key'] = String(requestParameters['xIdempotencyKey']);
        }

        if (this.configuration && this.configuration.accessToken) {
            // oauth required
            headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", ["investments:write"]);
        }

        const response = await this.request({
            path: `/api/external/distributor/v2/invitations/`,
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: ExternalInvitationRequestToJSON(requestParameters['externalInvitationRequest']),
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => ExternalInvitationFromJSON(jsonValue));
    }

    /**
     * Use this endpoint to create invitations for investors to invest into a product. After all needed data is present in the invitation, it can be processed into an investment.  Invitations can only be used if you are using the Investment Platform API offering. If you\'re using the Distributor API product offering you can directly create investments without the need for invitations.
     * Create an invitation
     */
    async externalDistributorV2InvitationsCreate(requestParameters: ExternalDistributorV2InvitationsCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<ExternalInvitation> {
        const response = await this.externalDistributorV2InvitationsCreateRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * You can use this endpoint to update an invitation. Use the ```required_survey_waiver``` and ```required_self_disclosure``` fields to determine what values need to be provided by investors during the investment process.
     * Update an invitation
     */
    async externalDistributorV2InvitationsPartialUpdateRaw(requestParameters: ExternalDistributorV2InvitationsPartialUpdateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<ExternalInvitation>> {
        if (requestParameters['xIdempotencyKey'] == null) {
            throw new runtime.RequiredError(
                'xIdempotencyKey',
                'Required parameter "xIdempotencyKey" was null or undefined when calling externalDistributorV2InvitationsPartialUpdate().'
            );
        }

        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling externalDistributorV2InvitationsPartialUpdate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        if (requestParameters['xIdempotencyKey'] != null) {
            headerParameters['X-Idempotency-Key'] = String(requestParameters['xIdempotencyKey']);
        }

        if (this.configuration && this.configuration.accessToken) {
            // oauth required
            headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", ["investments:write"]);
        }

        const response = await this.request({
            path: `/api/external/distributor/v2/invitations/{id}/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'PATCH',
            headers: headerParameters,
            query: queryParameters,
            body: PatchedExternalInvitationRequestToJSON(requestParameters['patchedExternalInvitationRequest']),
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => ExternalInvitationFromJSON(jsonValue));
    }

    /**
     * You can use this endpoint to update an invitation. Use the ```required_survey_waiver``` and ```required_self_disclosure``` fields to determine what values need to be provided by investors during the investment process.
     * Update an invitation
     */
    async externalDistributorV2InvitationsPartialUpdate(requestParameters: ExternalDistributorV2InvitationsPartialUpdateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<ExternalInvitation> {
        const response = await this.externalDistributorV2InvitationsPartialUpdateRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Use this endpoint to retrieve an invitation via ID.  ### Costs document   After the invitation has been created, the response will yield a costs document that need to be displayed to your investors before investing. It transparently shows which fees needs to be paid by the investor and the issuer.
     * Retrieve an invitation
     */
    async externalDistributorV2InvitationsRetrieveRaw(requestParameters: ExternalDistributorV2InvitationsRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<ExternalInvitation>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling externalDistributorV2InvitationsRetrieve().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        if (this.configuration && this.configuration.accessToken) {
            // oauth required
            headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", ["investments:read"]);
        }

        const response = await this.request({
            path: `/api/external/distributor/v2/invitations/{id}/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => ExternalInvitationFromJSON(jsonValue));
    }

    /**
     * Use this endpoint to retrieve an invitation via ID.  ### Costs document   After the invitation has been created, the response will yield a costs document that need to be displayed to your investors before investing. It transparently shows which fees needs to be paid by the investor and the issuer.
     * Retrieve an invitation
     */
    async externalDistributorV2InvitationsRetrieve(requestParameters: ExternalDistributorV2InvitationsRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<ExternalInvitation> {
        const response = await this.externalDistributorV2InvitationsRetrieveRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async externalDistributorV2InvitationsUpdateRaw(requestParameters: ExternalDistributorV2InvitationsUpdateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<ExternalInvitation>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling externalDistributorV2InvitationsUpdate().'
            );
        }

        if (requestParameters['externalInvitationRequest'] == null) {
            throw new runtime.RequiredError(
                'externalInvitationRequest',
                'Required parameter "externalInvitationRequest" was null or undefined when calling externalDistributorV2InvitationsUpdate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        if (this.configuration && this.configuration.accessToken) {
            // oauth required
            headerParameters["Authorization"] = await this.configuration.accessToken("oauth2", ["investments:write"]);
        }

        const response = await this.request({
            path: `/api/external/distributor/v2/invitations/{id}/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'PUT',
            headers: headerParameters,
            query: queryParameters,
            body: ExternalInvitationRequestToJSON(requestParameters['externalInvitationRequest']),
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => ExternalInvitationFromJSON(jsonValue));
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async externalDistributorV2InvitationsUpdate(requestParameters: ExternalDistributorV2InvitationsUpdateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<ExternalInvitation> {
        const response = await this.externalDistributorV2InvitationsUpdateRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsAbortionCreateRaw(requestParameters: InvitationsAbortionCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling invitationsAbortionCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        if (this.configuration && this.configuration.accessToken) {
            const token = this.configuration.accessToken;
            const tokenString = await token("jwtAuth", []);

            if (tokenString) {
                headerParameters["Authorization"] = `Bearer ${tokenString}`;
            }
        }
        const response = await this.request({
            path: `/api/invitations/{id}/abortion/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsAbortionCreate(requestParameters: InvitationsAbortionCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.invitationsAbortionCreateRaw(requestParameters, initOverrides);
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsAssetPreparationDestroyRaw(requestParameters: InvitationsAssetPreparationDestroyRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling invitationsAssetPreparationDestroy().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        if (this.configuration && this.configuration.accessToken) {
            const token = this.configuration.accessToken;
            const tokenString = await token("jwtAuth", []);

            if (tokenString) {
                headerParameters["Authorization"] = `Bearer ${tokenString}`;
            }
        }
        const response = await this.request({
            path: `/api/invitations/{id}/asset-preparation/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'DELETE',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsAssetPreparationDestroy(requestParameters: InvitationsAssetPreparationDestroyRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.invitationsAssetPreparationDestroyRaw(requestParameters, initOverrides);
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsAssetPreparationRetrieveRaw(requestParameters: InvitationsAssetPreparationRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<ManagedAssetPreparation>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling invitationsAssetPreparationRetrieve().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        if (this.configuration && this.configuration.accessToken) {
            const token = this.configuration.accessToken;
            const tokenString = await token("jwtAuth", []);

            if (tokenString) {
                headerParameters["Authorization"] = `Bearer ${tokenString}`;
            }
        }
        const response = await this.request({
            path: `/api/invitations/{id}/asset-preparation/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => ManagedAssetPreparationFromJSON(jsonValue));
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsAssetPreparationRetrieve(requestParameters: InvitationsAssetPreparationRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<ManagedAssetPreparation> {
        const response = await this.invitationsAssetPreparationRetrieveRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsAssetPreparationUpdateRaw(requestParameters: InvitationsAssetPreparationUpdateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<ManagedAssetPreparation>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling invitationsAssetPreparationUpdate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        if (this.configuration && this.configuration.accessToken) {
            const token = this.configuration.accessToken;
            const tokenString = await token("jwtAuth", []);

            if (tokenString) {
                headerParameters["Authorization"] = `Bearer ${tokenString}`;
            }
        }
        const response = await this.request({
            path: `/api/invitations/{id}/asset-preparation/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'PUT',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => ManagedAssetPreparationFromJSON(jsonValue));
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsAssetPreparationUpdate(requestParameters: InvitationsAssetPreparationUpdateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<ManagedAssetPreparation> {
        const response = await this.invitationsAssetPreparationUpdateRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsCalculationsRetrieveRaw(requestParameters: InvitationsCalculationsRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<InvestmentCalculation>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling invitationsCalculationsRetrieve().'
            );
        }

        const queryParameters: any = {};

        if (requestParameters['code'] != null) {
            queryParameters['code'] = requestParameters['code'];
        }

        if (requestParameters['requestedTokens'] != null) {
            queryParameters['requested_tokens'] = requestParameters['requestedTokens'];
        }

        const headerParameters: runtime.HTTPHeaders = {};

        if (this.configuration && this.configuration.accessToken) {
            const token = this.configuration.accessToken;
            const tokenString = await token("jwtAuth", []);

            if (tokenString) {
                headerParameters["Authorization"] = `Bearer ${tokenString}`;
            }
        }
        const response = await this.request({
            path: `/api/invitations/{id}/calculations/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => InvestmentCalculationFromJSON(jsonValue));
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsCalculationsRetrieve(requestParameters: InvitationsCalculationsRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<InvestmentCalculation> {
        const response = await this.invitationsCalculationsRetrieveRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Retrieve an identification. Create it if it does not exists.
     */
    async invitationsIdentificationRetrieveRaw(requestParameters: InvitationsIdentificationRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<KycInformation>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling invitationsIdentificationRetrieve().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        if (this.configuration && this.configuration.accessToken) {
            const token = this.configuration.accessToken;
            const tokenString = await token("jwtAuth", []);

            if (tokenString) {
                headerParameters["Authorization"] = `Bearer ${tokenString}`;
            }
        }
        const response = await this.request({
            path: `/api/invitations/{id}/identification/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => KycInformationFromJSON(jsonValue));
    }

    /**
     * Retrieve an identification. Create it if it does not exists.
     */
    async invitationsIdentificationRetrieve(requestParameters: InvitationsIdentificationRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<KycInformation> {
        const response = await this.invitationsIdentificationRetrieveRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Set the KYC status of an identification to \'started\'.
     */
    async invitationsIdentificationStartNotificationCreateRaw(requestParameters: InvitationsIdentificationStartNotificationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling invitationsIdentificationStartNotificationCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        if (this.configuration && this.configuration.accessToken) {
            const token = this.configuration.accessToken;
            const tokenString = await token("jwtAuth", []);

            if (tokenString) {
                headerParameters["Authorization"] = `Bearer ${tokenString}`;
            }
        }
        const response = await this.request({
            path: `/api/invitations/{id}/identification-start-notification/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * Set the KYC status of an identification to \'started\'.
     */
    async invitationsIdentificationStartNotificationCreate(requestParameters: InvitationsIdentificationStartNotificationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.invitationsIdentificationStartNotificationCreateRaw(requestParameters, initOverrides);
    }

    /**
     * list invitations
     */
    async invitationsListRaw(requestParameters: InvitationsListRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<PaginatedInvitationList>> {
        const queryParameters: any = {};

        if (requestParameters['campaignId'] != null) {
            queryParameters['campaign_id'] = requestParameters['campaignId'];
        }

        if (requestParameters['invitationType'] != null) {
            queryParameters['invitation_type'] = requestParameters['invitationType'];
        }

        if (requestParameters['isEcsp'] != null) {
            queryParameters['is_ecsp'] = requestParameters['isEcsp'];
        }

        if (requestParameters['limit'] != null) {
            queryParameters['limit'] = requestParameters['limit'];
        }

        if (requestParameters['offset'] != null) {
            queryParameters['offset'] = requestParameters['offset'];
        }

        if (requestParameters['ordering'] != null) {
            queryParameters['ordering'] = requestParameters['ordering'];
        }

        if (requestParameters['status'] != null) {
            queryParameters['status'] = requestParameters['status'];
        }

        const headerParameters: runtime.HTTPHeaders = {};

        if (this.configuration && this.configuration.accessToken) {
            const token = this.configuration.accessToken;
            const tokenString = await token("jwtAuth", []);

            if (tokenString) {
                headerParameters["Authorization"] = `Bearer ${tokenString}`;
            }
        }
        const response = await this.request({
            path: `/api/invitations/`,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => PaginatedInvitationListFromJSON(jsonValue));
    }

    /**
     * list invitations
     */
    async invitationsList(requestParameters: InvitationsListRequest = {}, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<PaginatedInvitationList> {
        const response = await this.invitationsListRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsOfferConfigurationCreateRaw(requestParameters: InvitationsOfferConfigurationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling invitationsOfferConfigurationCreate().'
            );
        }

        if (requestParameters['invitationOfferConfigurationRequest'] == null) {
            throw new runtime.RequiredError(
                'invitationOfferConfigurationRequest',
                'Required parameter "invitationOfferConfigurationRequest" was null or undefined when calling invitationsOfferConfigurationCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        if (this.configuration && this.configuration.accessToken) {
            const token = this.configuration.accessToken;
            const tokenString = await token("jwtAuth", []);

            if (tokenString) {
                headerParameters["Authorization"] = `Bearer ${tokenString}`;
            }
        }
        const response = await this.request({
            path: `/api/invitations/{id}/offer-configuration/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: InvitationOfferConfigurationRequestToJSON(requestParameters['invitationOfferConfigurationRequest']),
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsOfferConfigurationCreate(requestParameters: InvitationsOfferConfigurationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.invitationsOfferConfigurationCreateRaw(requestParameters, initOverrides);
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsOfferCreateRaw(requestParameters: InvitationsOfferCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling invitationsOfferCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        if (this.configuration && this.configuration.accessToken) {
            const token = this.configuration.accessToken;
            const tokenString = await token("jwtAuth", []);

            if (tokenString) {
                headerParameters["Authorization"] = `Bearer ${tokenString}`;
            }
        }
        const response = await this.request({
            path: `/api/invitations/{id}/offer/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsOfferCreate(requestParameters: InvitationsOfferCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.invitationsOfferCreateRaw(requestParameters, initOverrides);
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsPrefilledQuestionnaireListRaw(requestParameters: InvitationsPrefilledQuestionnaireListRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<Array<QuestionnaireQuestion>>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling invitationsPrefilledQuestionnaireList().'
            );
        }

        const queryParameters: any = {};

        if (requestParameters['ordering'] != null) {
            queryParameters['ordering'] = requestParameters['ordering'];
        }

        const headerParameters: runtime.HTTPHeaders = {};

        if (this.configuration && this.configuration.accessToken) {
            const token = this.configuration.accessToken;
            const tokenString = await token("jwtAuth", []);

            if (tokenString) {
                headerParameters["Authorization"] = `Bearer ${tokenString}`;
            }
        }
        const response = await this.request({
            path: `/api/invitations/{id}/prefilled-questionnaire/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => jsonValue.map(QuestionnaireQuestionFromJSON));
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsPrefilledQuestionnaireList(requestParameters: InvitationsPrefilledQuestionnaireListRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<Array<QuestionnaireQuestion>> {
        const response = await this.invitationsPrefilledQuestionnaireListRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsPrefilledVibInfoRetrieveRaw(requestParameters: InvitationsPrefilledVibInfoRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<VibInfo>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling invitationsPrefilledVibInfoRetrieve().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        if (this.configuration && this.configuration.accessToken) {
            const token = this.configuration.accessToken;
            const tokenString = await token("jwtAuth", []);

            if (tokenString) {
                headerParameters["Authorization"] = `Bearer ${tokenString}`;
            }
        }
        const response = await this.request({
            path: `/api/invitations/{id}/prefilled-vib-info/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => VibInfoFromJSON(jsonValue));
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsPrefilledVibInfoRetrieve(requestParameters: InvitationsPrefilledVibInfoRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<VibInfo> {
        const response = await this.invitationsPrefilledVibInfoRetrieveRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsQuestionnaireCreateRaw(requestParameters: InvitationsQuestionnaireCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<Invitation>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling invitationsQuestionnaireCreate().'
            );
        }

        if (requestParameters['invitationSetQuestionnaireRequest'] == null) {
            throw new runtime.RequiredError(
                'invitationSetQuestionnaireRequest',
                'Required parameter "invitationSetQuestionnaireRequest" was null or undefined when calling invitationsQuestionnaireCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        if (this.configuration && this.configuration.accessToken) {
            const token = this.configuration.accessToken;
            const tokenString = await token("jwtAuth", []);

            if (tokenString) {
                headerParameters["Authorization"] = `Bearer ${tokenString}`;
            }
        }
        const response = await this.request({
            path: `/api/invitations/{id}/questionnaire/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: InvitationSetQuestionnaireRequestToJSON(requestParameters['invitationSetQuestionnaireRequest']),
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => InvitationFromJSON(jsonValue));
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsQuestionnaireCreate(requestParameters: InvitationsQuestionnaireCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<Invitation> {
        const response = await this.invitationsQuestionnaireCreateRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Create the investor.
     */
    async invitationsRegistrationCreateRaw(requestParameters: InvitationsRegistrationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<InvestorCreation>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling invitationsRegistrationCreate().'
            );
        }

        if (requestParameters['investorCreationRequest'] == null) {
            throw new runtime.RequiredError(
                'investorCreationRequest',
                'Required parameter "investorCreationRequest" was null or undefined when calling invitationsRegistrationCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        if (this.configuration && this.configuration.accessToken) {
            const token = this.configuration.accessToken;
            const tokenString = await token("jwtAuth", []);

            if (tokenString) {
                headerParameters["Authorization"] = `Bearer ${tokenString}`;
            }
        }
        const response = await this.request({
            path: `/api/invitations/{id}/registration/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: InvestorCreationRequestToJSON(requestParameters['investorCreationRequest']),
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => InvestorCreationFromJSON(jsonValue));
    }

    /**
     * Create the investor.
     */
    async invitationsRegistrationCreate(requestParameters: InvitationsRegistrationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<InvestorCreation> {
        const response = await this.invitationsRegistrationCreateRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Update the registration
     */
    async invitationsRegistrationUpdateRaw(requestParameters: InvitationsRegistrationUpdateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling invitationsRegistrationUpdate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        if (this.configuration && this.configuration.accessToken) {
            const token = this.configuration.accessToken;
            const tokenString = await token("jwtAuth", []);

            if (tokenString) {
                headerParameters["Authorization"] = `Bearer ${tokenString}`;
            }
        }
        const response = await this.request({
            path: `/api/invitations/{id}/registration/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'PUT',
            headers: headerParameters,
            query: queryParameters,
            body: InvestorUpdateRequestToJSON(requestParameters['investorUpdateRequest']),
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * Update the registration
     */
    async invitationsRegistrationUpdate(requestParameters: InvitationsRegistrationUpdateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.invitationsRegistrationUpdateRaw(requestParameters, initOverrides);
    }

    /**
     * If before setting up a wallet, an investor decides to choose another wallet type, then this endpoint needs to be called to reset the `add_wallet_later` field.
     */
    async invitationsResetWalletSelectionCreateRaw(requestParameters: InvitationsResetWalletSelectionCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling invitationsResetWalletSelectionCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        if (this.configuration && this.configuration.accessToken) {
            const token = this.configuration.accessToken;
            const tokenString = await token("jwtAuth", []);

            if (tokenString) {
                headerParameters["Authorization"] = `Bearer ${tokenString}`;
            }
        }
        const response = await this.request({
            path: `/api/invitations/{id}/reset-wallet-selection/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * If before setting up a wallet, an investor decides to choose another wallet type, then this endpoint needs to be called to reset the `add_wallet_later` field.
     */
    async invitationsResetWalletSelectionCreate(requestParameters: InvitationsResetWalletSelectionCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.invitationsResetWalletSelectionCreateRaw(requestParameters, initOverrides);
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsRetrieveRaw(requestParameters: InvitationsRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<Invitation>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling invitationsRetrieve().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        if (this.configuration && this.configuration.accessToken) {
            const token = this.configuration.accessToken;
            const tokenString = await token("jwtAuth", []);

            if (tokenString) {
                headerParameters["Authorization"] = `Bearer ${tokenString}`;
            }
        }
        const response = await this.request({
            path: `/api/invitations/{id}/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => InvitationFromJSON(jsonValue));
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsRetrieve(requestParameters: InvitationsRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<Invitation> {
        const response = await this.invitationsRetrieveRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsSelfDisclosureRetrieveRaw(requestParameters: InvitationsSelfDisclosureRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<SelfDisclosureCalculation>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling invitationsSelfDisclosureRetrieve().'
            );
        }

        const queryParameters: any = {};

        if (requestParameters['requestedTokens'] != null) {
            queryParameters['requested_tokens'] = requestParameters['requestedTokens'];
        }

        const headerParameters: runtime.HTTPHeaders = {};

        if (this.configuration && this.configuration.accessToken) {
            const token = this.configuration.accessToken;
            const tokenString = await token("jwtAuth", []);

            if (tokenString) {
                headerParameters["Authorization"] = `Bearer ${tokenString}`;
            }
        }
        const response = await this.request({
            path: `/api/invitations/{id}/self-disclosure/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => SelfDisclosureCalculationFromJSON(jsonValue));
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsSelfDisclosureRetrieve(requestParameters: InvitationsSelfDisclosureRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<SelfDisclosureCalculation> {
        const response = await this.invitationsSelfDisclosureRetrieveRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Determines if the survey submitted for an invitation has the related score above the minimum score for each of the survey groups
     */
    async invitationsSurveyScoreListRaw(requestParameters: InvitationsSurveyScoreListRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<Array<GroupScore>>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling invitationsSurveyScoreList().'
            );
        }

        const queryParameters: any = {};

        if (requestParameters['ordering'] != null) {
            queryParameters['ordering'] = requestParameters['ordering'];
        }

        const headerParameters: runtime.HTTPHeaders = {};

        if (this.configuration && this.configuration.accessToken) {
            const token = this.configuration.accessToken;
            const tokenString = await token("jwtAuth", []);

            if (tokenString) {
                headerParameters["Authorization"] = `Bearer ${tokenString}`;
            }
        }
        const response = await this.request({
            path: `/api/invitations/{id}/survey-score/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => jsonValue.map(GroupScoreFromJSON));
    }

    /**
     * Determines if the survey submitted for an invitation has the related score above the minimum score for each of the survey groups
     */
    async invitationsSurveyScoreList(requestParameters: InvitationsSurveyScoreListRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<Array<GroupScore>> {
        const response = await this.invitationsSurveyScoreListRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsTrackingInformationCreateRaw(requestParameters: InvitationsTrackingInformationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling invitationsTrackingInformationCreate().'
            );
        }

        if (requestParameters['invitationTrackingRequest'] == null) {
            throw new runtime.RequiredError(
                'invitationTrackingRequest',
                'Required parameter "invitationTrackingRequest" was null or undefined when calling invitationsTrackingInformationCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        if (this.configuration && this.configuration.accessToken) {
            const token = this.configuration.accessToken;
            const tokenString = await token("jwtAuth", []);

            if (tokenString) {
                headerParameters["Authorization"] = `Bearer ${tokenString}`;
            }
        }
        const response = await this.request({
            path: `/api/invitations/{id}/tracking-information/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: InvitationTrackingRequestToJSON(requestParameters['invitationTrackingRequest']),
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsTrackingInformationCreate(requestParameters: InvitationsTrackingInformationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.invitationsTrackingInformationCreateRaw(requestParameters, initOverrides);
    }

    /**
     * especially needed for campaign invitations which cannot be associated with a user automatically due to lack of an email address.
     */
    async invitationsUserAssociationCreateRaw(requestParameters: InvitationsUserAssociationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling invitationsUserAssociationCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        if (this.configuration && this.configuration.accessToken) {
            const token = this.configuration.accessToken;
            const tokenString = await token("jwtAuth", []);

            if (tokenString) {
                headerParameters["Authorization"] = `Bearer ${tokenString}`;
            }
        }
        const response = await this.request({
            path: `/api/invitations/{id}/user-association/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * especially needed for campaign invitations which cannot be associated with a user automatically due to lack of an email address.
     */
    async invitationsUserAssociationCreate(requestParameters: InvitationsUserAssociationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.invitationsUserAssociationCreateRaw(requestParameters, initOverrides);
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsVibInfoCreateRaw(requestParameters: InvitationsVibInfoCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling invitationsVibInfoCreate().'
            );
        }

        if (requestParameters['invitationSetVibInfoRequest'] == null) {
            throw new runtime.RequiredError(
                'invitationSetVibInfoRequest',
                'Required parameter "invitationSetVibInfoRequest" was null or undefined when calling invitationsVibInfoCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        if (this.configuration && this.configuration.accessToken) {
            const token = this.configuration.accessToken;
            const tokenString = await token("jwtAuth", []);

            if (tokenString) {
                headerParameters["Authorization"] = `Bearer ${tokenString}`;
            }
        }
        const response = await this.request({
            path: `/api/invitations/{id}/vib-info/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: InvitationSetVibInfoRequestToJSON(requestParameters['invitationSetVibInfoRequest']),
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsVibInfoCreate(requestParameters: InvitationsVibInfoCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.invitationsVibInfoCreateRaw(requestParameters, initOverrides);
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsVoucherRetrieveRaw(requestParameters: InvitationsVoucherRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<Voucher>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling invitationsVoucherRetrieve().'
            );
        }

        const queryParameters: any = {};

        if (requestParameters['code'] != null) {
            queryParameters['code'] = requestParameters['code'];
        }

        if (requestParameters['requestedTokens'] != null) {
            queryParameters['requested_tokens'] = requestParameters['requestedTokens'];
        }

        const headerParameters: runtime.HTTPHeaders = {};

        if (this.configuration && this.configuration.accessToken) {
            const token = this.configuration.accessToken;
            const tokenString = await token("jwtAuth", []);

            if (tokenString) {
                headerParameters["Authorization"] = `Bearer ${tokenString}`;
            }
        }
        const response = await this.request({
            path: `/api/invitations/{id}/voucher/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => VoucherFromJSON(jsonValue));
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsVoucherRetrieve(requestParameters: InvitationsVoucherRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<Voucher> {
        const response = await this.invitationsVoucherRetrieveRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsWaiversCreateRaw(requestParameters: InvitationsWaiversCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling invitationsWaiversCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        if (this.configuration && this.configuration.accessToken) {
            const token = this.configuration.accessToken;
            const tokenString = await token("jwtAuth", []);

            if (tokenString) {
                headerParameters["Authorization"] = `Bearer ${tokenString}`;
            }
        }
        const response = await this.request({
            path: `/api/invitations/{id}/waivers/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: WaiverRequestToJSON(requestParameters['waiverRequest']),
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsWaiversCreate(requestParameters: InvitationsWaiversCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.invitationsWaiversCreateRaw(requestParameters, initOverrides);
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsWalletAssociationCreateRaw(requestParameters: InvitationsWalletAssociationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling invitationsWalletAssociationCreate().'
            );
        }

        if (requestParameters['investmentWalletAssociationRequest'] == null) {
            throw new runtime.RequiredError(
                'investmentWalletAssociationRequest',
                'Required parameter "investmentWalletAssociationRequest" was null or undefined when calling invitationsWalletAssociationCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        if (this.configuration && this.configuration.accessToken) {
            const token = this.configuration.accessToken;
            const tokenString = await token("jwtAuth", []);

            if (tokenString) {
                headerParameters["Authorization"] = `Bearer ${tokenString}`;
            }
        }
        const response = await this.request({
            path: `/api/invitations/{id}/wallet-association/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: InvestmentWalletAssociationRequestToJSON(requestParameters['investmentWalletAssociationRequest']),
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * Mixin to add TX retry functionality to classes inheriting from APIView, such as GenericAPIView.  By default handlers are assumed TX safe, and thus will be retryable.
     */
    async invitationsWalletAssociationCreate(requestParameters: InvitationsWalletAssociationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.invitationsWalletAssociationCreateRaw(requestParameters, initOverrides);
    }

    /**
     * Select a wallet type without creating a wallet immediately. Used for mobile wallet onboarding.
     */
    async invitationsWalletSelectionCreateRaw(requestParameters: InvitationsWalletSelectionCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling invitationsWalletSelectionCreate().'
            );
        }

        if (requestParameters['investmentWalletSelectionRequest'] == null) {
            throw new runtime.RequiredError(
                'investmentWalletSelectionRequest',
                'Required parameter "investmentWalletSelectionRequest" was null or undefined when calling invitationsWalletSelectionCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        if (this.configuration && this.configuration.accessToken) {
            const token = this.configuration.accessToken;
            const tokenString = await token("jwtAuth", []);

            if (tokenString) {
                headerParameters["Authorization"] = `Bearer ${tokenString}`;
            }
        }
        const response = await this.request({
            path: `/api/invitations/{id}/wallet-selection/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: InvestmentWalletSelectionRequestToJSON(requestParameters['investmentWalletSelectionRequest']),
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * Select a wallet type without creating a wallet immediately. Used for mobile wallet onboarding.
     */
    async invitationsWalletSelectionCreate(requestParameters: InvitationsWalletSelectionCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.invitationsWalletSelectionCreateRaw(requestParameters, initOverrides);
    }

}
